export const buildQuery = (
  obj: Record<string, string | number | boolean | null> | null,
) => {
  if (!obj || Object.keys(obj).length === 0) return "";
  const result = Object.keys(obj)
    .map((key) => {
      if (obj[key]) {
        const value: string | number | boolean = obj[key] || "";
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
      return null;
    })
    .join("&");
  return `?${result}`;
};

export const getUrlSearchParams = (urlString: string): URLSearchParams => {
  const url = new URL(urlString);
  return url.searchParams;
};

export const getPathFromUrl = (urlString: string): string => {
  try {
    const url = new URL(urlString);
    return url.pathname;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    //  doesn't throw when we have dodgy of incomplete data with integration
    return urlString;
  }
};

/**
 * Simple utility function to return the value of a specific query parameter. Returns null if the value is not set, or
 * if the current URL is not set (i.e. for server side rendering).
 *
 * @param searchString
 * @param queryParameter
 * @return string|null
 */
export const getQueryValueFromUrlSearchString = (
  searchString: string,
  queryParameter: string,
): string | null => new URLSearchParams(searchString).get(queryParameter);

// Query might be string, string array or undefined, when we want to figure out slug for a url always want cast it as a string.
export const queryAsString = (query?: string | string[] | null): string => {
  if (typeof query === "undefined" || !query) {
    return "";
  }
  return Array.isArray(query) ? query.join("/") : query;
};

// Query might be string, string array or undefined, when we want to figure out slug for a url always want cast it as a string.
export const queryAsArray = (query?: string | string[] | null): string[] => {
  if (typeof query === "undefined" || !query) {
    return [];
  }
  return Array.isArray(query) ? query : [query];
};

export const removeURLParam = (urlString: string, param: string): string => {
  const url = new URL(urlString);
  url.searchParams.delete(param);
  return url.toString();
};

/**
 * When redirected to FWA from elsewhere we may have query parameters in the url
 * that determines the user journey and the app behaviour.
 * This single function collates all the relevant query parameters from all possible journeys
 * and returns them in a single object with named params for ease of use.
 */
export function redirectStateFromUrl(urlString: string): {
  isAmJourney: boolean;
  isEmJourney: boolean;
  isFromEmail: boolean;
  numberOfPages: number;
  amMessageHeader: string | null;
  amMessageBody: string | null;
  signature: string | null;
  forename: string | null;
  eventName: string | null;
  reservationId: string | null;
  waveCode: string | null;
} {
  const url = new URL(urlString);
  const searchParams = url.searchParams;
  // Activities flags
  const isAmJourney = searchParams.has("signature");
  // Events flags
  const isEmJourney = searchParams.get("em") === "1";
  const numberOfPages = parseInt(searchParams.get("np") || "0", 10);
  // From flags
  const isFromEmail = searchParams.get("from") === "email";
  // Activities values
  const signature = searchParams.get("signature");
  const amMessageHeader = searchParams.get("message_header");
  const amMessageBody = searchParams.get("message_body");
  // Events values
  const forename = searchParams.get("fn");
  const eventName = searchParams.get("ev");
  const reservationId = searchParams.get("rid");
  const waveCode = searchParams.get("wc");

  return {
    isAmJourney,
    isEmJourney,
    isFromEmail,
    numberOfPages,
    amMessageHeader,
    amMessageBody,
    signature,
    forename,
    eventName,
    reservationId,
    waveCode,
  };
}

export function urlParamsObjectClearAmParams(
  params: Record<string, string | string[] | undefined>,
) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { signature, message_header, message_body, ...restParams } = params;
  return restParams;
}

export function urlParamsObjectClearEmParams(
  params: Record<string, string | string[] | undefined>,
) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { em, np, fn, ev, wc, rid, ...restParams } = params;
  return restParams;
}

export function urlParamsObjectClearFromParam(
  params: Record<string, string | string[] | undefined>,
) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { from, ...restParams } = params;
  return restParams;
}

export function urlParamsObjectAddEmParams(
  params: Record<string, string | string[] | undefined>,
  {
    numberOfPages,
    forename,
    eventName,
    reservationId,
    waveCode,
  }: {
    numberOfPages: number;
    forename?: string;
    eventName?: string;
    reservationId?: string;
    waveCode?: string;
  },
) {
  return {
    ...params,
    em: 1, // flag to say we came from event journey
    np: numberOfPages, // has multiple pages
    // only pass these params if we have them
    ...(forename ? { fn: forename } : {}), // forename
    ...(eventName ? { ev: eventName } : {}), // event name
    ...(reservationId ? { rid: reservationId } : {}), // reservation id
    ...(waveCode ? { wc: waveCode } : {}), // wave code
  };
}
